import SvgIcon from '@mui/material/SvgIcon';
import Chip from '@mui/material/Chip';
import Badge from '@mui/material/Badge';
import { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { formatCountBadge } from '../../../util/formatCountBadge';

/**
 * Resolves `primary`, `error.main`, hex/rgb strings, etc. to a concrete CSS background color.
 * Raw palette keys like `primary` are not valid in `sx` and previously produced low-contrast chips.
 */
function resolveChipBackgroundColor(theme, color) {
  if (color == null || color === '') return theme.palette.primary.main;
  if (typeof color !== 'string') return theme.palette.primary.main;
  const trimmed = color.trim();
  if (trimmed.startsWith('#') || trimmed.startsWith('rgb')) return trimmed;
  const segments = trimmed.split('.');
  if (segments.length === 1) {
    const key = segments[0];
    const slot = theme.palette[key];
    if (slot && typeof slot === 'object' && slot.main != null) return slot.main;
  }
  let node = theme.palette;
  for (const seg of segments) {
    node = node?.[seg];
  }
  if (typeof node === 'string') return node;
  return theme.palette.primary.main;
}

export const NumberChip = ({
  number,
  size = 'small',
  color = 'primary',
  variant = 'filled',
  max = 99,
}) => {
  const theme = useTheme();
  const backgroundColor = useMemo(() => resolveChipBackgroundColor(theme, color), [theme, color]);
  const labelColor = useMemo(
    () => theme.palette.getContrastText(backgroundColor),
    [theme, backgroundColor]
  );

  return (
    <Chip
      label={formatCountBadge(number, max)}
      size={size}
      variant={variant}
      sx={{
        backgroundColor,
        color: labelColor,
        minWidth: size === 'small' ? '24px' : '32px',
        height: size === 'small' ? '24px' : '32px',
        borderRadius: '12px',
        '& .MuiChip-label': {
          px: size === 'small' ? 0.75 : 1,
          fontSize: size === 'small' ? '0.85rem' : '0.975rem',
          fontWeight: 600,
          lineHeight: 1,
          color: labelColor,
        },
      }}
    />
  );
};

export const NumberBadge = ({
  number,
  children,
  color = 'error',
  variant = 'standard',
  invisible = false,
}) => (
  <Badge
    badgeContent={number}
    color={color}
    variant={variant}
    invisible={invisible}
    sx={{
      '& .MuiBadge-badge': {
        fontSize: '0.75rem',
        fontWeight: 600,
        minWidth: '20px',
        height: '20px',
        borderRadius: '10px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
        border: '2px solid white',
      },
    }}
  >
    {children}
  </Badge>
);

export const GreaterThanIcon = ({ color }) => (
  <SvgIcon>
    <rect x='0' y='0' width='24' height='24' rx='8' ry='8' fill={color} />
    <text x='12' y='18' fontSize='18' textAnchor='middle' fill='black'>
      &gt;
    </text>
  </SvgIcon>
);
